import type {
  SecureFile,
  SecureFileDownloadedBy,
  SecureFileEvent,
} from "features/secureUploads/secureUploads";

export type FileAction =
  | "details"
  | "preview"
  | "download"
  | "delete"
  | "restore"
  | "add_to_project"
  | "add_to_collection"
  | "move_file"
  | "move_to_project";

export interface FileMenuItem {
  action: FileAction;
  label: string;
  /** A write the viewer may not make here (Client View). */
  readOnly?: boolean;
  danger?: boolean;
}

/** The row menu of a file; Download is the row's own button beside it. A deleted
 * file is looked at and, by Manage, restored; deleting needs the row's own
 * permission. Writes stay visible but off in Client View. */
export function fileMenu(
  file: Pick<
    SecureFile,
    | "can_delete"
    | "is_deleted"
    | "can_restore"
    | "project_id"
    | "collection_id"
    | "project_status"
  >,
  readOnly: boolean,
  canOrganize = false,
): FileMenuItem[] {
  // Staff move files - never out of Deleted Files or through a closed project.
  const moves =
    canOrganize && !file.is_deleted && file.project_status !== "closed";
  const items: FileMenuItem[] = [];
  if (moves && file.project_id == null)
    items.push({ action: "add_to_project", label: "Add to Project", readOnly });
  items.push(
    { action: "details", label: "View Details" },
    { action: "preview", label: "Preview" },
  );
  if (moves && file.project_id != null)
    items.push(
      file.collection_id == null
        ? { action: "add_to_collection", label: "Add to Collection", readOnly }
        : { action: "move_file", label: "Move File", readOnly },
      { action: "move_to_project", label: "Move to Project", readOnly },
    );
  if (file.is_deleted && file.can_restore)
    items.push({ action: "restore", label: "Restore", readOnly });
  if (!file.is_deleted && file.can_delete)
    items.push({
      action: "delete",
      label: "Delete File",
      danger: true,
      readOnly,
    });
  return items;
}

/** "Sept 24, 2026 11:56 PM". */
export function dateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** The Downloaded By cell: "Dana Reyes +1" over the date, or "Not yet". */
export function downloadedByLines(
  downloaded: SecureFileDownloadedBy | null,
): { who: string; when: string } | null {
  if (!downloaded) return null;
  const others = downloaded.others > 0 ? ` +${downloaded.others}` : "";
  return { who: `${downloaded.name}${others}`, when: dateTime(downloaded.at) };
}

export function fileSize(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, index);
  return `${value.toFixed(index ? 1 : 0)} ${units[index]}`;
}

export type FileSortColumn = "file_name" | "added_by" | "downloaded";

export interface FileSort {
  column: FileSortColumn;
  direction: "asc" | "desc";
}

/** Same column turns around; a new one starts A-Z, the latest download first. */
export function nextFileSort(
  current: FileSort | null,
  column: FileSortColumn,
): FileSort {
  if (current?.column === column)
    return { column, direction: current.direction === "asc" ? "desc" : "asc" };
  return { column, direction: column === "downloaded" ? "desc" : "asc" };
}

const EVENT_LABELS: Record<SecureFileEvent["kind"], string> = {
  uploaded: "Added",
  previewed: "Previewed",
  downloaded: "Downloaded",
  moved: "Moved",
  deleted: "Deleted",
  restored: "Restored",
};

/** "Moved from Payroll to Audit", or the plain verb. */
export function eventLine(event: SecureFileEvent): string {
  if (event.kind === "moved" && (event.moved_from || event.moved_to))
    return `Moved from ${event.moved_from || "-"} to ${event.moved_to || "-"}`;
  return EVENT_LABELS[event.kind] ?? event.kind;
}

/** Who will see a restored file back in Individual Files; null when it goes back
 * into a project. */
export function restoreAudience(
  file: Pick<SecureFile, "restore_to" | "is_confidential" | "client_name">,
): string | null {
  const target = file.restore_to;
  if (!target?.individual) return null;
  if (target.shared_only)
    return "Only the person who added it and the people it is shared with will see it in Individual Files.";
  return file.is_confidential
    ? `Everyone at ${file.client_name} with Confidential Access will see it in Individual Files.`
    : `Everyone at ${file.client_name} with Secure Exchange access will see it in Individual Files.`;
}
